import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Shield, Edit, Trash2 } from 'lucide-react';

export default function UserAccessRBAC({ user, fetchRbacUsers, fetchRoles }) {
  // Extract stable user properties to prevent infinite loops
  const userId = user?.user?._id;
  const userToken = user?.token;
  const userRole = user?.user?.role;

  useEffect(() => {
    // Only fetch RBAC data if user is admin
    if (userId && userToken && userRole === 'admin') {
      fetchRbacUsers(userId, userToken);
      fetchRoles(userId, userToken);
    }
  }, [userId, userToken, userRole, fetchRbacUsers, fetchRoles]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">User Access & RBAC</h2>
        <Button className="bg-blue-600 hover:bg-blue-700">
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>User Management</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  <div>
                    <h3 className="font-medium">John Doe</h3>
                    <p className="text-sm text-gray-600">john.doe@company.com</p>
                  </div>
                  <Badge variant="outline" className="text-blue-600 border-blue-600">
                    Admin
                  </Badge>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center space-x-4">
                  <div>
                    <h3 className="font-medium">Jane Smith</h3>
                    <p className="text-sm text-gray-600">jane.smith@company.com</p>
                  </div>
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    Operator
                  </Badge>
                </div>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Role Permissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 border rounded-lg">
                <div className="flex items-center mb-3">
                  <Shield className="w-5 h-5 text-blue-600 mr-2" />
                  <h3 className="font-medium">Admin</h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Full system access</li>
                  <li>• User management</li>
                  <li>• Cluster management</li>
                  <li>• Security settings</li>
                </ul>
              </div>
              
              <div className="p-4 border rounded-lg">
                <div className="flex items-center mb-3">
                  <Shield className="w-5 h-5 text-green-600 mr-2" />
                  <h3 className="font-medium">Operator</h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Device monitoring</li>
                  <li>• Basic operations</li>
                  <li>• View dashboards</li>
                  <li>• Generate reports</li>
                </ul>
              </div>
              
              <div className="p-4 border rounded-lg">
                <div className="flex items-center mb-3">
                  <Shield className="w-5 h-5 text-yellow-600 mr-2" />
                  <h3 className="font-medium">Viewer</h3>
                </div>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Read-only access</li>
                  <li>• View dashboards</li>
                  <li>• View reports</li>
                  <li>• No modifications</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick User Creation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Input placeholder="Full Name" />
              <Input placeholder="Email Address" type="email" />
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Select Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="operator">Operator</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
              <Button className="bg-green-600 hover:bg-green-700">
                Create User
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 